import React, { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { BackNavIcon, EyeIconButton } from "../../../../assets/common-svg/CommonSvg";

const ReadOnlyField = ({
    label,
    value,
    isFullRow = false,
}) => (
    <Grid item xs={12} md={isFullRow ? 12 : 6}>
        <Typography className="pb-1 font-medium">{label}</Typography>
        <div className="border border-gray-200 rounded-md px-3 py-2 bg-[#f8f9fa] min-h-[42px] flex items-center">
            <Typography className="text-sm">{value || "-"}</Typography>
        </div>
    </Grid>
);

const getMessageValue = (row) => row?.disclaimer_message || row?.message || row?.messaege || "";

const getMessagePreview = (htmlMessage, wordLimit = 8) => {
    if (!htmlMessage) return "-";
    const plainText = String(htmlMessage).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (!plainText) return "-";
    const words = plainText.split(" ");
    if (words.length <= wordLimit) return plainText;
    return `${words.slice(0, wordLimit).join(" ")}...`;
};

export default function ViewLog() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { state } = useLocation();
    const [openMessageModal, setOpenMessageModal] = useState(false);

    const row = useMemo(() => state?.row || {}, [state]);
    const messageHtml = useMemo(() => getMessageValue(row), [row]);
    const messagePreview = useMemo(() => getMessagePreview(messageHtml, 8), [messageHtml]);

    const fields = [
        { label: "Disclaimer Log ID", value: row.id || id },
        { label: "Name", value: row.name },
        { label: "Email", value: row.email },
        { label: "Role", value: row.role_title },
        { label: "Title", value: row.title, isFullRow: true },
        { label: "Message", type: "message", isFullRow: true },
        { label: "Location", value: row.location },
        { label: "IP Address", value: row.ip_address },
        { label: "Date", value: row.date_time?.date || row.date },
        { label: "Time", value: row.date_time?.time || row.time },
    ];

    return (
        <div className="!p-6">
            <button
                className="cursor-pointer flex gap-2"
                onClick={() => navigate("/bmr/master/logs")}
            >
                <BackNavIcon /> Back
            </button>

            <div className="!p-4 mt-3 bg-white flex flex-col gap-4 rounded-2xl">
                <Typography variant="h5">View Disclaimer Log</Typography>
                <Grid container spacing={2}>
                    {fields.map((field) => {
                        if (field.type === "message") {
                            return (
                                <Grid item xs={12} key={field.label}>
                                    <Typography className="pb-1 font-medium">Message</Typography>
                                    <div className="border border-gray-200 rounded-md px-3 py-2 bg-[#f8f9fa] min-h-[42px] flex items-center justify-between">
                                        <Tooltip title="Click on view icon for full message" placement="top">
                                            <Typography className="text-sm truncate pr-2">
                                                {messagePreview}
                                            </Typography>
                                        </Tooltip>
                                        {messageHtml ? (
                                            <Tooltip title="View message">
                                                <IconButton onClick={() => setOpenMessageModal(true)}>
                                                    <EyeIconButton bgColor={"#3571FF"} />
                                                </IconButton>
                                            </Tooltip>
                                        ) : null}
                                    </div>
                                </Grid>
                            );
                        }

                        return (
                            <ReadOnlyField
                                key={field.label}
                                label={field.label}
                                value={field.value}
                                isFullRow={field.isFullRow}
                            />
                        );
                    })}
                </Grid>
            </div>

            <Dialog
                open={openMessageModal}
                onClose={() => setOpenMessageModal(false)}
                fullWidth
                maxWidth="xl"
                PaperProps={{
                    sx: { width: "85vw", maxWidth: "1305px",margin:"5px" },
                }}
            >
                <DialogTitle className="flex items-center justify-between">
                    <span>Disclaimer Message</span>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenMessageModal(false)}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {messageHtml ? (
                        <div
                            className="prose max-w-none"
                            dangerouslySetInnerHTML={{ __html: messageHtml }}
                        />
                    ) : (
                        <Typography>-</Typography>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
